import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';
import { FieldLabelMark } from './Input';

export interface DateFieldProps {
  /** Selected date as `yyyy-MM-dd`, or null when empty. Matches native `<input type="date">` value. */
  value: string | null;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  /** Marks the control invalid when a shared/group-level error is rendered elsewhere. */
  invalid?: boolean;
  /** Earliest selectable date, `yyyy-MM-dd`. Earlier days are disabled. */
  min?: string;
  /** Latest selectable date, `yyyy-MM-dd`. Later days are disabled (e.g. date of birth → today). */
  max?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  requiredMark?: boolean;
  optional?: boolean;
  /** BCP-47 locale for month/weekday names and the trigger label. Defaults to the runtime locale. */
  locale?: string;
  placement?: 'bottom' | 'top';
  className?: string;
  /** Caller-supplied (translatable) accessible labels — this package stays i18n-free. */
  triggerAriaLabel?: string;
  prevMonthLabel?: string;
  nextMonthLabel?: string;
  /** Accessible label for the year dropdown (lets you jump years fast, e.g. for date of birth). */
  yearLabel?: string;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Local `yyyy-MM-dd` for a Date (no timezone shift, unlike toISOString). */
function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseDateStr(s: string | null | undefined): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * DateField — pure presentational date picker (design-system styled calendar popover).
 *
 * Controlled: reports the chosen day as `yyyy-MM-dd` via `onChange`, owns no business rules.
 * `min`/`max` gate which days are selectable (past-blocking on bookings, future-blocking on DOB).
 * Month/weekday names come from `Intl` (locale-aware); all fixed accessible strings are caller-supplied
 * so the consuming app can translate them. Colours/spacing derive entirely from design-system tokens.
 */
export function DateField({
  value,
  onChange,
  label,
  error,
  invalid,
  min,
  max,
  placeholder,
  id,
  disabled,
  requiredMark,
  optional,
  locale,
  placement = 'bottom',
  className,
  triggerAriaLabel,
  prevMonthLabel = 'Previous month',
  nextMonthLabel = 'Next month',
  yearLabel = 'Year',
}: DateFieldProps) {
  const reactId = useId();
  const fieldId = id ?? `date-field-${reactId}`;
  const errorId = `${fieldId}-error`;

  const selected = parseDateStr(value);
  const [open, setOpen] = useState(false);
  const [yearView, setYearView] = useState(false);
  const [view, setView] = useState(() => {
    const base = selected ?? parseDateStr(min) ?? new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });
  const containerRef = useRef<HTMLDivElement>(null);

  // When opening, snap the calendar to the selected month (or today) so the current value is visible.
  //
  // Adjusted during render on the open→closed transition rather than in an effect: React re-runs this component
  // immediately with the corrected state and never commits the stale view, so the calendar cannot flash the
  // previous month. (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      const base = parseDateStr(value) ?? parseDateStr(min) ?? new Date();
      setView({ year: base.getFullYear(), month: base.getMonth() });
      setYearView(false);
    }
  }

  // Close on outside pointer press.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const weekdayNames = useMemo(() => {
    const base = new Date(2023, 0, 1); // A Sunday.
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return d.toLocaleDateString(locale, { weekday: 'narrow' });
    });
  }, [locale]);

  const firstOfMonth = new Date(view.year, view.month, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const monthName = firstOfMonth.toLocaleDateString(locale, { month: 'long' });
  const monthTitle = firstOfMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const todayStr = toDateStr(new Date());

  // Year dropdown range — bounded by min/max when given so it can't offer out-of-range years
  // (e.g. date of birth caps at today), otherwise a generous window around the current year.
  const years = useMemo(() => {
    const thisYear = new Date().getFullYear();
    const start = min ? Number(min.slice(0, 4)) : thisYear - 120;
    const end = max ? Number(max.slice(0, 4)) : thisYear + 10;
    const lo = Math.min(start, view.year);
    const hi = Math.max(end, view.year);
    return Array.from({ length: hi - lo + 1 }, (_, i) => hi - i); // newest first
  }, [min, max, view.year]);

  const triggerText = selected
    ? selected.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    : placeholder;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const shiftMonth = (delta: number) => {
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const pick = (day: number) => {
    onChange(toDateStr(new Date(view.year, view.month, day)));
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-col gap-[7px]', className)}>
      {label && (
        <label htmlFor={fieldId} className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]">
          {label}
          <FieldLabelMark requiredMark={requiredMark} optional={optional} />
        </label>
      )}

      <div className="relative" ref={containerRef} onKeyDown={onKeyDown}>
        <button
          id={fieldId}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={triggerAriaLabel}
          aria-invalid={invalid || error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'flex h-12 w-full items-center gap-2.5 rounded-[12px] border px-[15px] text-left',
            'transition-[background-color,border-color,box-shadow] duration-200',
            'bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--text)]',
            'disabled:cursor-not-allowed disabled:opacity-60',
            open ? 'border-[var(--primary)] bg-[var(--surface)] ring-4 ring-[var(--ring)]' : 'border-transparent',
            error && 'border-[var(--danger)]'
          )}
        >
          <svg className="h-4 w-4 shrink-0 text-[var(--text-3)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="16" rx="2" />
            <path strokeLinecap="round" d="M3 9h18M8 2.5v4M16 2.5v4" />
          </svg>
          <span className={cn('min-w-0 flex-1 truncate text-[14.5px]', selected ? 'text-[var(--text)]' : 'text-[var(--text-3)]')}>
            {triggerText}
          </span>
        </button>

        {open && (
          <div
            role="dialog"
            aria-label={monthTitle}
            className={cn(
              'absolute left-0 z-50 w-[288px] rounded-[13px] border border-[var(--border)] bg-[var(--surface)] p-3 shadow-[var(--shadow-3)]',
              'animate-[fadeIn_0.14s_ease-out]',
              placement === 'top' ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]'
            )}
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                aria-label={prevMonthLabel}
                onClick={() => shiftMonth(-1)}
                className="grid h-8 w-8 place-items-center rounded-[8px] text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              {/* Tap the month/year to switch into the year grid — fast for far-back dates like DOB. */}
              <button
                type="button"
                aria-label={yearLabel}
                aria-expanded={yearView}
                onClick={() => setYearView((v) => !v)}
                className={cn(
                  'flex items-center gap-1 rounded-[8px] px-2 py-1 text-[13.5px] font-semibold text-[var(--text)] transition-colors hover:bg-[var(--surface-2)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]',
                  yearView && 'bg-[var(--surface-2)]'
                )}
              >
                {monthName} {view.year}
                <svg className={cn('h-3.5 w-3.5 text-[var(--text-3)] transition-transform', yearView && 'rotate-180')} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                aria-label={nextMonthLabel}
                onClick={() => shiftMonth(1)}
                className="grid h-8 w-8 place-items-center rounded-[8px] text-[var(--text-2)] transition-colors hover:bg-[var(--surface-2)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>

            {yearView ? (
              <div role="listbox" aria-label={yearLabel} className="grid max-h-[232px] grid-cols-4 gap-1 overflow-y-auto p-0.5">
                {years.map((y) => {
                  const isCurrent = y === view.year;
                  return (
                    <button
                      key={y}
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      onClick={() => {
                        setView((v) => ({ ...v, year: y }));
                        setYearView(false);
                      }}
                      className={cn(
                        'rounded-[8px] py-2 text-[13px] font-semibold tabular-nums transition-colors',
                        'text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
                        isCurrent && 'bg-[var(--primary)] text-[var(--primary-fg)] hover:bg-[var(--primary-hover)] hover:text-[var(--primary-fg)]'
                      )}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="mb-1 grid grid-cols-7 gap-0.5">
                  {weekdayNames.map((name, i) => (
                    <span key={i} aria-hidden="true" className="grid h-8 place-items-center text-[11px] font-semibold text-[var(--text-3)]">
                      {name}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-0.5">
                  {Array.from({ length: startWeekday }, (_, i) => (
                    <span key={`pad-${i}`} aria-hidden="true" />
                  ))}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const dateStr = toDateStr(new Date(view.year, view.month, day));
                    const isDisabled = (min != null && dateStr < min) || (max != null && dateStr > max);
                    const isSelected = dateStr === value;
                    const isToday = dateStr === todayStr;
                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={isDisabled}
                        aria-current={isToday ? 'date' : undefined}
                        aria-pressed={isSelected}
                        onClick={() => pick(day)}
                        className={cn(
                          'grid h-9 place-items-center rounded-[8px] text-[13px] tabular-nums transition-colors',
                          'text-[var(--text)] hover:bg-[var(--surface-2)]',
                          'disabled:cursor-not-allowed disabled:text-[var(--text-3)] disabled:opacity-40 disabled:hover:bg-transparent',
                          isToday && !isSelected && 'font-bold text-[var(--primary)]',
                          isSelected && 'bg-[var(--primary)] font-semibold text-[var(--primary-fg)] hover:bg-[var(--primary-hover)]'
                        )}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <span id={errorId} role="alert" className="flex items-center gap-1 text-[12px] font-medium text-[var(--danger)]">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
          </svg>
          {error}
        </span>
      )}
    </div>
  );
}
